/**
 * QuickFitPanel - Slide-out panel for aircraft-to-unit fit analysis
 *
 * Supports two input modes:
 * - "Model" tab: select aircraft type from the catalog
 * - "Tail #" tab: select existing aircraft by tail number
 *
 * Results show fit status with CTA buttons:
 * - Add Plane: opens wizard pre-filled with type + unit
 * - Test Fit: places a temp aircraft for visual verification
 *
 * Clicking an area header zooms the camera to that hangar.
 */

import { useState, useCallback, useMemo } from 'react'
import {
  Ruler,
  Check,
  AlertTriangle,
  X,
  Plane,
  Plus,
  FlaskConical,
  Building2,
  DoorOpen,
  Eye,
  EyeOff,
  Filter,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AircraftTypeAutocomplete } from '@/components/aircraft-types/AircraftTypeAutocomplete'
import { useRampPanelStore } from '@/stores/rampPanelStore'
import { useAddPlaneWizardStore, type AircraftDimensions } from '@/stores/addPlaneWizardStore'
import { useQuickFit } from '@/hooks/useQuickFit'
import { useAircraftList } from '@/hooks/useAircraft'
import type { AircraftTypeRow } from '@/types/aircraft-types'
import type { AircraftTableRow } from '@/types/aircrafts'
import type { FitStatus, FailReason, FitAircraftDims, QuickFitCandidate } from '@/types/quick-fit'

type InputMode = 'model' | 'tail'

interface QuickFitPanelProps {
  locationId: string | null
  onFocusArea?: (areaId: string) => void
}

// ============================================
// Labels & Helpers
// ============================================

const FAIL_REASON_LABELS: Record<FailReason, string> = {
  wingspan_exceeds_door: 'Wingspan exceeds door width',
  height_exceeds_door: 'Height exceeds door height',
  wingspan_exceeds_max: 'Wingspan exceeds max',
  length_exceeds_max: 'Length exceeds max',
  tail_height_exceeds_max: 'Tail height exceeds max',
  no_space: 'Insufficient floor space',
  unit_full: 'Unit is full',
}

const FIT_STATUS_CONFIG: Record<FitStatus, { label: string; className: string; icon: React.ElementType }> = {
  fits: {
    label: 'Fits',
    className: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20',
    icon: Check,
  },
  tight: {
    label: 'Tight',
    className: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
    icon: AlertTriangle,
  },
  'no-fit': {
    label: 'No Fit',
    className: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20',
    icon: X,
  },
}

function formatFt(value: number | null): string {
  if (value == null) return 'N/A'
  return `${value.toFixed(1)}'`
}

function formatMargin(value: number | null): string {
  if (value == null) return ''
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}'`
}

function marginColor(value: number | null): string {
  if (value == null) return 'text-muted-foreground'
  if (value < 0) return 'text-red-600 dark:text-red-400'
  if (value < 3) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-green-600 dark:text-green-400'
}

/** Extract normalized dims from an aircraft type row */
function dimsFromType(t: AircraftTypeRow): FitAircraftDims {
  return { wingspan: t.wingspan, length: t.length, height: t.height, tailHeight: t.tail_height, footprintSqft: t.footprint_sqft }
}

/** Extract normalized dims from an individual aircraft row (returns null if missing) */
function dimsFromAircraft(a: AircraftTableRow): FitAircraftDims | null {
  if (a.wingspan == null || a.length == null || a.height == null) return null
  return { wingspan: a.wingspan, length: a.length, height: a.height, tailHeight: a.tail_height, footprintSqft: null }
}

/** Build wizard AircraftDimensions from FitAircraftDims */
function toWizardDims(d: FitAircraftDims): AircraftDimensions {
  return { wingspan: d.wingspan, length: d.length, height: d.height, tail_height: d.tailHeight }
}

// ============================================
// Sub-components
// ============================================

function DimsSummary({ dims }: { dims: FitAircraftDims }) {
  const buffered = (dims.footprintSqft ?? dims.wingspan * dims.length) * 1.05
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0 text-[11px] text-muted-foreground bg-muted/50 rounded px-2.5 py-1">
      <span><span className="font-medium text-foreground">{formatFt(dims.wingspan)}</span> ws</span>
      <span><span className="font-medium text-foreground">{formatFt(dims.length)}</span> len</span>
      <span><span className="font-medium text-foreground">{formatFt(dims.height)}</span> h</span>
      {dims.tailHeight != null && (
        <span><span className="font-medium text-foreground">{formatFt(dims.tailHeight)}</span> tail</span>
      )}
      <span className="text-muted-foreground/40">|</span>
      <span>
        <span className="font-medium text-foreground">{Math.round(buffered).toLocaleString()}</span> sqft{dims.footprintSqft == null ? ' est.' : ''} <span className="opacity-60">+5%</span>
      </span>
    </div>
  )
}

function SummaryBar({ fits, tight, noFit }: { fits: number; tight: number; noFit: number }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1 text-[11px]">
      <span className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        <span className="font-medium">{fits}</span> fit{fits !== 1 ? 's' : ''}
      </span>
      <span className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
        <span className="font-medium">{tight}</span> tight
      </span>
      <span className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        <span className="font-medium">{noFit}</span> blocked
      </span>
    </div>
  )
}

// ============================================
// Tail Number Search
// ============================================

function TailNumberSearch({
  onSelect,
  selectedAircraft,
}: {
  onSelect: (aircraft: AircraftTableRow) => void
  selectedAircraft: AircraftTableRow | null
}) {
  const [query, setQuery] = useState('')
  const { data: allAircraft, isLoading } = useAircraftList()

  const results = useMemo(() => {
    if (!allAircraft || query.length < 1) return []
    const q = query.toUpperCase()
    return allAircraft
      .filter((a) => a.tail_number?.toUpperCase().includes(q))
      .slice(0, 10)
  }, [allAircraft, query])

  const displayValue = selectedAircraft
    ? `${selectedAircraft.tail_number} — ${selectedAircraft.aircraft_type_manufacturer ?? ''} ${selectedAircraft.aircraft_type_model ?? ''}`.trim()
    : null

  return (
    <div className="space-y-1.5">
      <input
        type="text"
        value={selectedAircraft ? '' : query}
        onChange={(e) => setQuery(e.target.value.toUpperCase())}
        placeholder={displayValue ?? 'Search tail number...'}
        className={cn(
          'w-full h-9 px-3 text-sm border rounded-md bg-background',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          selectedAircraft && 'placeholder:text-foreground'
        )}
        onFocus={() => {
          if (selectedAircraft) {
            setQuery(selectedAircraft.tail_number)
          }
        }}
      />

      {/* Selected indicator */}
      {selectedAircraft && (
        <button
          onClick={() => onSelect(null as unknown as AircraftTableRow)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Clear selection
        </button>
      )}

      {/* Dropdown results */}
      {!selectedAircraft && query.length >= 1 && results.length > 0 && (
        <div className="border rounded-md overflow-hidden max-h-48 overflow-y-auto">
          {results.map((aircraft) => {
            const hasDims = aircraft.wingspan != null && aircraft.length != null && aircraft.height != null
            return (
              <button
                key={aircraft.id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/50 flex items-center justify-between gap-2"
                onClick={() => {
                  onSelect(aircraft)
                  setQuery('')
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Plane className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                  <span className="font-mono font-medium">{aircraft.tail_number}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {aircraft.aircraft_type_manufacturer} {aircraft.aircraft_type_model}
                  </span>
                </div>
                {!hasDims && (
                  <span className="text-[10px] text-yellow-600 shrink-0">No dims</span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {!selectedAircraft && query.length >= 1 && results.length === 0 && !isLoading && (
        <div className="text-xs text-muted-foreground py-2 text-center">
          No aircraft found for "{query}"
        </div>
      )}
    </div>
  )
}

// ============================================
// Candidate Card with CTA buttons
// ============================================

function CandidateCard({
  candidate,
  onAddPlane,
  onTestFit,
}: {
  candidate: QuickFitCandidate
  onAddPlane: (candidate: QuickFitCandidate) => void
  onTestFit: (candidate: QuickFitCandidate) => void
}) {
  const statusConfig = FIT_STATUS_CONFIG[candidate.fitStatus]
  const StatusIcon = statusConfig.icon
  const isNoFit = candidate.fitStatus === 'no-fit'
  const canPlace = !isNoFit

  return (
    <div
      className={cn(
        'px-4 py-2.5 border-b last:border-b-0 transition-colors',
        isNoFit && 'opacity-50'
      )}
    >
      {/* Row 1: Unit code + fit badge */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <DoorOpen className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-mono text-sm font-medium">{candidate.unitCode}</span>
          {candidate.unitType && (
            <span className="text-xs text-muted-foreground">{candidate.unitType}</span>
          )}
        </div>
        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-5', statusConfig.className)}>
          <StatusIcon className="w-3 h-3 mr-0.5" />
          {statusConfig.label}
        </Badge>
      </div>

      {/* Row 2: Dimensions + margins */}
      <div className="ml-5.5 text-xs space-y-0.5">
        {(candidate.doorWidth != null || candidate.doorHeight != null) && (
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground w-10">Door:</span>
            {candidate.doorWidth != null && (
              <span>
                {formatFt(candidate.doorWidth)}w{' '}
                <span className={marginColor(candidate.wingspanDoorMargin)}>
                  ({formatMargin(candidate.wingspanDoorMargin)})
                </span>
              </span>
            )}
            {candidate.doorHeight != null && (
              <span>
                {formatFt(candidate.doorHeight)}h{' '}
                <span className={marginColor(candidate.heightDoorMargin)}>
                  ({formatMargin(candidate.heightDoorMargin)})
                </span>
              </span>
            )}
          </div>
        )}

        {(candidate.maxWingspan != null || candidate.maxLength != null) && (
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground w-10">Max:</span>
            {candidate.maxWingspan != null && (
              <span>
                {formatFt(candidate.maxWingspan)} span{' '}
                <span className={marginColor(candidate.wingspanMaxMargin)}>
                  ({formatMargin(candidate.wingspanMaxMargin)})
                </span>
              </span>
            )}
            {candidate.maxLength != null && (
              <span>
                {formatFt(candidate.maxLength)} len{' '}
                <span className={marginColor(candidate.lengthMaxMargin)}>
                  ({formatMargin(candidate.lengthMaxMargin)})
                </span>
              </span>
            )}
          </div>
        )}

        {/* Sqft breakdown: total | used | projected */}
        {candidate.usableSqft != null && candidate.usableSqft > 0 ? (
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-muted-foreground">{Math.round(candidate.usableSqft).toLocaleString()}</span>
            <span className="text-muted-foreground/50">/</span>
            <span className={candidate.occupiedSqft > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}>
              {Math.round(candidate.occupiedSqft).toLocaleString()} used
            </span>
            {candidate.bufferedFootprint != null && candidate.fitStatus !== 'no-fit' && (
              <>
                <span className="text-muted-foreground/50">&rarr;</span>
                <span className={cn(
                  'font-medium',
                  candidate.capacityPctAfterFit != null && candidate.capacityPctAfterFit >= 70
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400'
                )}>
                  {Math.round(candidate.occupiedSqft + candidate.bufferedFootprint).toLocaleString()} after
                </span>
                {candidate.capacityPctAfterFit != null && (
                  <span className={cn(
                    'text-[10px]',
                    candidate.capacityPctAfterFit >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 'text-muted-foreground'
                  )}>
                    ({Math.round(candidate.capacityPctAfterFit)}%)
                  </span>
                )}
              </>
            )}
            <span className="text-[10px] text-muted-foreground/60">sqft</span>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Plane className="w-3 h-3" />
              {candidate.placedAircraftCount} placed
            </span>
          </div>
        )}

        {/* Extra context row */}
        <div className="flex items-center gap-3 text-xs">
          {candidate.usableSqft != null && candidate.usableSqft > 0 && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Plane className="w-3 h-3" />
              {candidate.placedAircraftCount} placed
            </span>
          )}
          {candidate.leaseClientName && (
            <span className="text-muted-foreground truncate max-w-[120px]">
              {candidate.leaseClientName}
            </span>
          )}
        </div>

        {candidate.failReasons.length > 0 && (
          <div className="text-red-600 dark:text-red-400">
            {candidate.failReasons.map((r) => FAIL_REASON_LABELS[r]).join(' · ')}
          </div>
        )}

        {/* CTA buttons */}
        {canPlace && (
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => onAddPlane(candidate)}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Plane
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => onTestFit(candidate)}
            >
              <FlaskConical className="w-3 h-3 mr-1" />
              Test Fit
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// Main Panel
// ============================================

export function QuickFitPanel({ locationId, onFocusArea }: QuickFitPanelProps) {
  const { activePanel, closePanel } = useRampPanelStore()
  const isOpen = activePanel === 'quick-fit'

  const [inputMode, setInputMode] = useState<InputMode>('model')
  const [showBlocked, setShowBlocked] = useState(true)
  const [unitFilter, setUnitFilter] = useState('')

  // Model mode state
  const [selectedType, setSelectedType] = useState<AircraftTypeRow | null>(null)

  // Tail number mode state
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftTableRow | null>(null)

  // Compute normalized dims + display name from whichever input mode
  const activeDims = useMemo<FitAircraftDims | null>(() => {
    if (inputMode === 'model' && selectedType) return dimsFromType(selectedType)
    if (inputMode === 'tail' && selectedAircraft) return dimsFromAircraft(selectedAircraft)
    return null
  }, [inputMode, selectedType, selectedAircraft])

  const displayName = useMemo(() => {
    if (inputMode === 'model' && selectedType) {
      return [selectedType.manufacturer, selectedType.model, selectedType.variant].filter(Boolean).join(' ')
    }
    if (inputMode === 'tail' && selectedAircraft) {
      return selectedAircraft.tail_number
    }
    return ''
  }, [inputMode, selectedType, selectedAircraft])

  const { result, isLoading } = useQuickFit(locationId, activeDims, displayName)

  // Group candidates by area (with optional text filter)
  const groupedCandidates = useMemo(() => {
    if (!result) return []
    let filtered = showBlocked
      ? result.candidates
      : result.candidates.filter((c) => c.fitStatus !== 'no-fit')

    if (unitFilter) {
      const q = unitFilter.toUpperCase()
      filtered = filtered.filter(
        (c) =>
          c.unitCode.toUpperCase().includes(q) ||
          c.areaName.toUpperCase().includes(q) ||
          c.areaCode.toUpperCase().includes(q)
      )
    }

    const groups = new Map<string, { areaId: string; areaName: string; areaCode: string; candidates: QuickFitCandidate[] }>()
    for (const c of filtered) {
      if (!groups.has(c.areaId)) {
        groups.set(c.areaId, { areaId: c.areaId, areaName: c.areaName, areaCode: c.areaCode, candidates: [] })
      }
      groups.get(c.areaId)!.candidates.push(c)
    }
    return Array.from(groups.values())
  }, [result, showBlocked, unitFilter])

  // --- CTA handlers ---

  const handleAddPlane = useCallback(
    (candidate: QuickFitCandidate) => {
      const store = useAddPlaneWizardStore.getState()

      if (inputMode === 'model' && selectedType && activeDims) {
        store.setAircraftType(selectedType.id, displayName, toWizardDims(activeDims))
      } else if (inputMode === 'tail' && selectedAircraft && activeDims) {
        store.setSelectedExistingAircraft(
          selectedAircraft.id,
          selectedAircraft.tail_number,
          toWizardDims(activeDims),
          displayName,
          selectedAircraft.owner_client_id,
          selectedAircraft.aircraft_type_default_fuel_type as 'jet_a' | 'avgas' | null
        )
      }

      // Pre-select unit/area so Location step (3) has it ready
      store.setSelectedArea(candidate.areaId)
      store.setSelectedUnit(candidate.unitId)
      store.setLocationMethod('dropdown')

      // Run full wizard — model mode needs tail number (step 0), tail mode skips to client (step 1)
      store.deactivateQuickPlace()
      store.openWizard()
      store.goToStep(inputMode === 'tail' ? 1 : 0)
      closePanel()
    },
    [inputMode, selectedType, selectedAircraft, activeDims, displayName, closePanel]
  )

  const handleTestFit = useCallback(
    (_candidate: QuickFitCandidate) => {
      const store = useAddPlaneWizardStore.getState()

      if (inputMode === 'model' && selectedType && activeDims) {
        store.setAircraftType(selectedType.id, displayName, toWizardDims(activeDims))
        store.setNoTailNumber(true)
      } else if (inputMode === 'tail' && selectedAircraft && activeDims) {
        store.setSelectedExistingAircraft(
          selectedAircraft.id,
          selectedAircraft.tail_number,
          toWizardDims(activeDims),
          displayName,
          selectedAircraft.owner_client_id,
          selectedAircraft.aircraft_type_default_fuel_type as 'jet_a' | 'avgas' | null
        )
      }

      store.activateQuickPlace()
      store.openWizard()
      closePanel()
    },
    [inputMode, selectedType, selectedAircraft, activeDims, displayName, closePanel]
  )

  const handleFocusArea = useCallback(
    (areaId: string) => {
      onFocusArea?.(areaId)
    },
    [onFocusArea]
  )

  const handleTailSelect = useCallback((aircraft: AircraftTableRow | null) => {
    setSelectedAircraft(aircraft)
  }, [])

  const hasSelection = activeDims != null

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closePanel()}>
      <SheetContent side="left" className="w-[380px] p-0">
        <SheetHeader className="px-3 py-1.5 border-b">
          <SheetTitle className="flex items-center gap-2 text-sm">
            <Ruler className="w-3.5 h-3.5" />
            Quick Fit
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-42px)] overflow-hidden">
          {/* Input mode tabs + aircraft selector */}
          <div className="px-3 pt-1 pb-1.5 border-b space-y-1.5">
            <div className="flex rounded-md border p-0.5 bg-muted/50">
              <button
                className={cn(
                  'flex-1 text-[11px] font-medium py-1 rounded transition-colors',
                  inputMode === 'model'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setInputMode('model')}
              >
                Model
              </button>
              <button
                className={cn(
                  'flex-1 text-[11px] font-medium py-1 rounded transition-colors',
                  inputMode === 'tail'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setInputMode('tail')}
              >
                Tail #
              </button>
            </div>
            {inputMode === 'model' && (
              <AircraftTypeAutocomplete
                onSelect={setSelectedType}
                value={selectedType?.id ?? null}
                placeholder="Search aircraft model..."
              />
            )}

            {inputMode === 'tail' && (
              <TailNumberSearch
                onSelect={handleTailSelect}
                selectedAircraft={selectedAircraft}
              />
            )}

            {/* Dimensions display */}
            {activeDims && <DimsSummary dims={activeDims} />}

            {/* Warning for aircraft without dimensions */}
            {inputMode === 'tail' && selectedAircraft && !activeDims && (
              <div className="text-[11px] text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 px-2.5 py-1.5 rounded">
                No dimensions recorded. Add wingspan, length and height to enable fit analysis.
              </div>
            )}
          </div>

          {/* Summary + toggle */}
          {result && (
            <div className="border-b flex items-center justify-between pr-1.5">
              <SummaryBar
                fits={result.summary.fits}
                tight={result.summary.tight}
                noFit={result.summary.noFit}
              />
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setShowBlocked(!showBlocked)}
                    >
                      {showBlocked ? (
                        <Eye className="w-3.5 h-3.5" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {showBlocked ? 'Hide blocked units' : 'Show blocked units'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Unit/area filter */}
          {result && (
            <div className="px-3 py-1 border-b">
              <div className="relative">
                <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={unitFilter}
                  onChange={(e) => setUnitFilter(e.target.value)}
                  placeholder="Filter by hangar or unit..."
                  className="w-full h-7 pl-7 pr-7 text-xs border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                />
                {unitFilter && (
                  <button
                    onClick={() => setUnitFilter('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          <ScrollArea className="flex-1 min-h-0">
            {!hasSelection && (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                {inputMode === 'model'
                  ? 'Select an aircraft model to check fit'
                  : 'Search for a tail number to check fit'}
              </div>
            )}

            {hasSelection && isLoading && (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                Loading spaces...
              </div>
            )}

            {hasSelection && !isLoading && result && groupedCandidates.length === 0 && (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                No matching units found
              </div>
            )}

            {groupedCandidates.map((group) => (
              <div key={group.areaId}>
                {/* Area header — clickable to zoom */}
                <button
                  className="sticky top-0 z-10 w-full bg-muted/80 backdrop-blur-sm px-4 py-1.5 flex items-center gap-2 hover:bg-muted transition-colors text-left"
                  onClick={() => handleFocusArea(group.areaId)}
                >
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="font-medium text-xs">{group.areaName}</span>
                  <span className="text-xs text-muted-foreground">{group.areaCode}</span>
                </button>

                {group.candidates.map((candidate) => (
                  <CandidateCard
                    key={candidate.unitId}
                    candidate={candidate}
                    onAddPlane={handleAddPlane}
                    onTestFit={handleTestFit}
                  />
                ))}
              </div>
            ))}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  )
}
